/**
 * pages/OAuthCallback.tsx
 * OAuth callback page for cloud sync providers
 *
 * This page receives the OAuth authorization response from cloud providers
 * (Google Drive, Dropbox, OneDrive, Box) and displays it to allow the parent
 * window to read the callback parameters (code, state, error).
 *
 * This page is opened in a popup window by OAuthCloudSyncProvider and should
 * NOT redirect anywhere. The parent window monitors this popup and extracts
 * the URL parameters to complete the OAuth flow.
 */

import { useEffect, useState } from 'react';
import { Card, CardBody } from '@enfomentis/ui';
import { CheckCircle, XCircle, Loader2 } from 'lucide-react';

/**
 * OAuth Callback Page
 *
 * Displays the OAuth callback status and allows parent window to read URL parameters.
 * Automatically closes after 3 seconds on success or error.
 */
export const OAuthCallback: React.FC = () => {
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading');
  const [message, setMessage] = useState<string>('Processing OAuth callback...');

  useEffect(() => {
    // Parse URL parameters
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');
    const errorDescription = params.get('error_description');

    // Determine status
    if (error) {
      setStatus('error');
      setMessage(errorDescription || 'OAuth authentication failed');
    } else if (code) {
      setStatus('success');
      setMessage('Authentication successful! This window will close automatically.');
    } else {
      setStatus('error');
      setMessage('Invalid OAuth callback - no code or error received');
    }

    // Auto-close after 3 seconds
    const timer = setTimeout(() => {
      window.close();
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardBody className="flex flex-col items-center gap-4 p-8">
          {/* Status Icon */}
          {status === 'loading' && (
            <Loader2 className="h-16 w-16 text-primary animate-spin" />
          )}
          {status === 'success' && (
            <CheckCircle className="h-16 w-16 text-success" />
          )}
          {status === 'error' && (
            <XCircle className="h-16 w-16 text-danger" />
          )}

          {/* Status Message */}
          <div className="text-center space-y-2">
            <h1 className="text-xl font-semibold text-text-primary">
              {status === 'loading' && 'Processing...'}
              {status === 'success' && 'Authentication Successful'}
              {status === 'error' && 'Authentication Failed'}
            </h1>
            <p className="text-sm text-text-secondary">
              {message}
            </p>
          </div>

          {/* Manual Close Button (fallback) */}
          {status !== 'loading' && (
            <button
              onClick={() => window.close()}
              className="text-sm text-text-tertiary hover:text-text-secondary underline"
            >
              Close this window manually
            </button>
          )}
        </CardBody>
      </Card>
    </div>
  );
};
